import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
  Tooltip,
  alpha,
  Chip,
  IconButton
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  SwapHoriz as UpgradeIcon,
  Repeat as RecurringIcon,
  Receipt as OneTimeIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  AccountTree as OwnerIcon,
  KeyboardArrowDown as ExpandedIcon,
  KeyboardArrowRight as CollapsedIcon
} from '@mui/icons-material';
import { buildCrmApiUrl } from '../../api/apiConfig';

const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;

// Owner view: non-owner buckets (Product / Product (Webinar) / Unassigned) are hidden.
// Admins see everything untouched.
const NON_OWNER_BUCKETS = new Set(['Product', 'Product (Webinar)', 'Unassigned']);

// Columns of the owner table. inTotalOnly marks the two figures a leader also gets for
// other teams (whose rows carry nothing else).
const REVENUE_COLUMNS = [
  { label: 'Revenue', inTotalOnly: true, render: (r) => formatCurrency(r.revenue) },
  { label: 'Trial', render: (r) => formatCurrency(r.trial_revenue) },
  { label: 'Recurring', render: (r) => formatCurrency(r.recurring_revenue) },
  { label: 'Monthly', render: (r) => formatCurrency(r.monthly_recurring_revenue) },
  { label: 'Quarterly', render: (r) => formatCurrency(r.quarterly_recurring_revenue) },
  { label: 'Yearly', render: (r) => formatCurrency(r.yearly_recurring_revenue) },
  { label: 'Other', render: (r) => formatCurrency(r.other_recurring_revenue) },
  { label: 'One-time', render: (r) => formatCurrency(r.one_time_revenue) },
  { label: 'Conv', inTotalOnly: true, render: (r) => r.conversions ?? 0 },
  { label: 'Trial C', render: (r) => `${r.trial_conversions_pre_kyc ?? 0} / ${r.trial_conversions_post_kyc ?? 0}` },
  { label: 'First', render: (r) => r.first_timer_count ?? 0 },
  { label: 'Upgrade', render: (r) => r.upgrade_count ?? 0 }
];

const NO_TEAM_KEY = '__no_team__';
// Opaque so the sticky first column covers the cells scrolling under it.
const TEAM_BG = '#f4f5f7';
const TEAM_MINE_BG = '#e9f1fd';

function getMetricsPeriodDates(period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const currentDayOfWeek = today.getDay();
  const daysFromMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
  const startOfCurrentWeek = new Date(today);
  startOfCurrentWeek.setDate(today.getDate() - daysFromMonday);

  switch (period) {
    case 'today':
      return { start_date: formatDate(today), end_date: formatDate(today) };
    case 'week4':
      return { start_date: formatDate(startOfCurrentWeek), end_date: formatDate(today) };
    case 'week3': {
      const week3End = new Date(startOfCurrentWeek);
      week3End.setDate(week3End.getDate() - 1);
      const week3Start = new Date(week3End);
      week3Start.setDate(week3Start.getDate() - 6);
      return { start_date: formatDate(week3Start), end_date: formatDate(week3End) };
    }
    case 'week2': {
      const week2End = new Date(startOfCurrentWeek);
      week2End.setDate(week2End.getDate() - 8);
      const week2Start = new Date(week2End);
      week2Start.setDate(week2Start.getDate() - 6);
      return { start_date: formatDate(week2Start), end_date: formatDate(week2End) };
    }
    case 'week1': {
      const week1End = new Date(startOfCurrentWeek);
      week1End.setDate(week1End.getDate() - 15);
      const week1Start = new Date(week1End);
      week1Start.setDate(week1Start.getDate() - 6);
      return { start_date: formatDate(week1Start), end_date: formatDate(week1End) };
    }
    case 'month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start_date: formatDate(startOfMonth), end_date: formatDate(today) };
    }
    case 'last_month': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start_date: formatDate(startOfLastMonth), end_date: formatDate(endOfLastMonth) };
    }
    default:
      return { start_date: formatDate(today), end_date: formatDate(today) };
  }
}

const MetricCard = ({ title, value, subtext, icon: Icon, color }) => {
  const theme = useTheme();
  const bg = alpha(color, 0.08);
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        background: bg,
        transition: 'all 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
            {title}
          </Typography>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.15)
            }}
          >
            <Icon sx={{ color, fontSize: 20 }} />
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ color: color, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
        {subtext && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtext}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const REVENUE_REFRESH_COOLDOWN_MS = 15 * 60 * 1000;
const REVENUE_LAST_FETCH_KEY = 'revenue_dashboard_last_fetch';

const RevenueDashboard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [datePreset, setDatePreset] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [refreshCooldown, setRefreshCooldown] = useState(0);
  const [userRole, setUserRole] = useState(null); // 'admin' | 'owner'
  const [salesRole, setSalesRole] = useState(''); // admin | leader | executive
  const [ownerName, setOwnerName] = useState(null); // when owner, for filtering
  const [collapsedTeams, setCollapsedTeams] = useState({}); // leader name -> true when folded

  const today = new Date();
  const formatDate = (d) => d.toISOString().slice(0, 10);

  const getActiveDates = () => {
    if (datePreset === 'custom') {
      return { startDate: customStart || formatDate(today), endDate: customEnd || formatDate(today) };
    }
    const { start_date, end_date } = getMetricsPeriodDates(datePreset);
    return { startDate: start_date, endDate: end_date };
  };

  const { startDate, endDate } = getActiveDates();

  const canRefresh = () => {
    const last = localStorage.getItem(REVENUE_LAST_FETCH_KEY);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) >= REVENUE_REFRESH_COOLDOWN_MS;
  };

  useEffect(() => {
    const number = localStorage.getItem('number');
    if (!number) { setUserRole('unauthorized'); return; }
    fetch(`${buildCrmApiUrl('owner-access/me')}?number=${encodeURIComponent(number)}`, {
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.data) {
          // The dashboard has two shapes: the full admin view, and the scoped one for
          // the sales team (an executive: own row + teammates; a leader: own team + other teams' totals).
          const r = d.data.role || 'unauthorized';
          setUserRole(r === 'leader' || r === 'executive' ? 'owner' : r);
          setSalesRole(r);
          setOwnerName(d.data.owner || null);
        } else {
          setUserRole('unauthorized');
        }
      })
      .catch(() => setUserRole('unauthorized'));
  }, []);

  const fetchMetrics = async (forceRefresh = false) => {
    if (userRole === 'unauthorized') return;
    if (forceRefresh && !canRefresh()) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (forceRefresh) params.set('force_refresh', 'true');
      if (userRole === 'owner') params.set('role', 'owner');
      const res = await fetch(
        `${buildCrmApiUrl('clients/metrics/revenue-dashboard')}?${params}`,
        { headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' } }
      );
      const data = await res.json();
      if (data.success) {
        setMetrics(data.data);
        if (forceRefresh) localStorage.setItem(REVENUE_LAST_FETCH_KEY, String(Date.now()));
      } else {
        setError(data.error || 'Failed to load metrics');
      }
    } catch (err) {
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (!canRefresh()) return;
    fetchMetrics(true);
  };

  useEffect(() => {
    if (userRole === null) return;
    if (userRole === 'unauthorized') { setLoading(false); return; }
    fetchMetrics();
  }, [startDate, endDate, userRole, ownerName]);

  useEffect(() => {
    const updateCooldown = () => {
      const last = localStorage.getItem(REVENUE_LAST_FETCH_KEY);
      if (!last) { setRefreshCooldown(0); return; }
      const remaining = Math.max(0, Math.ceil((REVENUE_REFRESH_COOLDOWN_MS - (Date.now() - parseInt(last, 10))) / 1000));
      setRefreshCooldown(remaining);
    };
    updateCooldown();
    const id = setInterval(updateCooldown, 1000);
    return () => clearInterval(id);
  }, [loading, metrics]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === 'custom') {
      const d = new Date(today);
      d.setDate(1);
      setCustomStart(formatDate(d));
      setCustomEnd(formatDate(today));
    }
  };

  if (userRole === 'unauthorized') {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography color="text.secondary" variant="h6">You don&apos;t have access to revenue data.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Contact an admin to get access.</Typography>
      </Paper>
    );
  }

  if (loading && !metrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Box textAlign="center">
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography color="text.secondary">Loading revenue metrics...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography color="error" variant="h6" gutterBottom>{error}</Typography>
        <Button variant="outlined" onClick={() => fetchMetrics()}>Retry</Button>
      </Paper>
    );
  }

  const s = metrics?.summary || {};
  const isOwnerView = userRole === 'owner';
  const ownerRows = (metrics?.by_owner || []).filter((row) => !isOwnerView || !NON_OWNER_BUCKETS.has(row.owner));
  // Admins and leaders get the rows grouped into teams; an executive gets one block of
  // their team's executives (no leader).
  const teams = Array.isArray(metrics?.teams) && metrics.teams.length > 0 ? metrics.teams : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
        }}
      >
        <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.02em' }}>
              Revenue Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Period revenue excludes recurring installment; installment shown separately.
            </Typography>
          </Box>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Tooltip title={refreshCooldown > 0 ? `Refresh in ${Math.floor(refreshCooldown / 60)}m ${refreshCooldown % 60}s` : 'Refresh (15 min cooldown)'}>
              <span>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={refreshCooldown > 0 || loading}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {refreshCooldown > 0 ? `${Math.floor(refreshCooldown / 60)}:${String(refreshCooldown % 60).padStart(2, '0')}` : 'Refresh'}
                </Button>
              </span>
            </Tooltip>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <TextField
                size="small"
                select
                value={datePreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                sx={{ minWidth: 180 }}
                slotProps={{ htmlInput: { sx: { borderRadius: 2 } } }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week1">Week 1</MenuItem>
                <MenuItem value="week2">Week 2</MenuItem>
                <MenuItem value="week3">Week 3</MenuItem>
                <MenuItem value="week4">Week 4 (Current)</MenuItem>
                <MenuItem value="month">Current Month</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
                <MenuItem value="custom">Custom Date</MenuItem>
              </TextField>
            </Box>
            {datePreset === 'custom' && (
              <Box display="flex" gap={1}>
                <TextField size="small" type="date" sx={{ width: 140 }} value={customStart} onChange={(e) => setCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" sx={{ width: 140 }} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Leader: own team's total next to whichever team is number one right now */}
      {isOwnerView && salesRole === 'leader' && metrics?.team_total && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title={`Your team (rank ${metrics.team_rank} of ${metrics.team_count})`}
              value={formatCurrency(metrics.team_total.revenue)}
              icon={MoneyIcon}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title={metrics.top_team?.leader === metrics.team_total.leader ? 'Leading team: yours' : `Leading team: ${metrics.top_team?.leader || '-'}`}
              value={formatCurrency(metrics.top_team?.revenue)}
              icon={StarIcon}
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>
      )}

      {/* Primary Metrics - admin only */}
      {!isOwnerView && (
      <>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'text.secondary' }}>Period Overview</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard title="Total Revenue" value={formatCurrency(s.total_revenue)} icon={MoneyIcon} color={theme.palette.success.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard title="Trial Revenue" value={formatCurrency(s.trial_revenue)} icon={StarIcon} color={theme.palette.warning.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard title="Recurring" value={formatCurrency(s.recurring_revenue)} icon={RecurringIcon} color={theme.palette.info.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard title="One-time" value={formatCurrency(s.one_time_revenue)} icon={OneTimeIcon} color={theme.palette.primary.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard title="Recurring Installment" value={formatCurrency(s.recurring_installment_revenue)} subtext="Period only" icon={RecurringIcon} color="#5c6bc0" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <MetricCard title="Conversions" value={s.conversions ?? 0} subtext={`Plan: ${s.plan_conversions ?? 0} · Webinar: ${s.webinar_conversions ?? 0}`} icon={PeopleIcon} color={theme.palette.success.main} />
        </Grid>
      </Grid>

      {/* Recurring Breakdown */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'text.secondary' }}>Recurring by Billing Cycle</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title="Monthly" value={formatCurrency(s.monthly_recurring_revenue)} icon={RecurringIcon} color={theme.palette.info.light} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title="Quarterly" value={formatCurrency(s.quarterly_recurring_revenue)} icon={RecurringIcon} color={theme.palette.info.main} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title="Yearly" value={formatCurrency(s.yearly_recurring_revenue)} icon={RecurringIcon} color={theme.palette.info.dark} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard title="Other" value={formatCurrency(s.other_recurring_revenue)} icon={RecurringIcon} color="#78909c" />
        </Grid>
      </Grid>

      {/* First Timers & Upgrades */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard title="First Timers" value={s.first_timer_count ?? 0} subtext={formatCurrency(s.first_timer_revenue)} icon={TrendingUpIcon} color="#9c27b0" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard title="Upgrades" value={s.upgrade_count ?? 0} subtext={formatCurrency(s.upgrade_revenue)} icon={UpgradeIcon} color="#00bcd4" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <MetricCard title="Trial Conversions" value={`${s.trial_conversions_pre_kyc ?? 0} / ${s.trial_conversions_post_kyc ?? 0}`} subtext="Pre-KYC / Post-KYC" icon={PeopleIcon} color={theme.palette.warning.main} />
        </Grid>
      </Grid>

      {/* Lifetime Recurring */}
      {metrics?.lifetime_recurring && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            background: alpha(theme.palette.info.main, 0.03)
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <RecurringIcon sx={{ color: 'info.main' }} />
            <Typography variant="h6" fontWeight={700}>Lifetime Recurring Revenue</Typography>
            <Chip label="All time" size="small" variant="outlined" sx={{ ml: 1 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No date filter. No owner split.</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard title="Monthly" value={formatCurrency(metrics.lifetime_recurring.monthly)} icon={RecurringIcon} color={theme.palette.info.light} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard title="Quarterly" value={formatCurrency(metrics.lifetime_recurring.quarterly)} icon={RecurringIcon} color={theme.palette.info.main} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard title="Yearly" value={formatCurrency(metrics.lifetime_recurring.yearly)} icon={RecurringIcon} color={theme.palette.info.dark} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard title="Other" value={formatCurrency(metrics.lifetime_recurring.other)} icon={RecurringIcon} color="#78909c" />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.08) }}>
            <Typography variant="h6" fontWeight={700} color="info.main">Total: {formatCurrency(metrics.lifetime_recurring.total)}</Typography>
          </Box>
        </Paper>
      )}
      </>
      )}

      {/* Owner Breakdown */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <OwnerIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" fontWeight={700}>{isOwnerView ? (salesRole === 'leader' ? 'Teams' : 'Your Revenue') : 'By Team'}</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 640, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 3, minWidth: 220 }}>{teams ? 'Team / Member' : 'Owner'}</TableCell>
                {REVENUE_COLUMNS.map((col) => (
                  <TableCell key={col.label} align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Executive: just their own row */}
              {!teams && ownerRows.map((row) => (
                <TableRow key={row.owner}>
                  <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>{row.owner}</TableCell>
                  {REVENUE_COLUMNS.map((col) => (
                    <TableCell key={col.label} align="right">{col.render(row)}</TableCell>
                  ))}
                </TableRow>
              ))}
              {!teams && ownerRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={REVENUE_COLUMNS.length + 1} align="center" sx={{ py: 3, color: 'text.secondary' }}>No revenue in this period.</TableCell>
                </TableRow>
              )}

              {/* Admin / leader: one block per team, the team total first and its people below */}
              {teams && teams.map((team, teamIdx) => {
                const key = team.leader || NO_TEAM_KEY;
                const members = team.members || [];
                const canExpand = team.detail && members.length > 0;
                const open = canExpand && !collapsedTeams[key];
                const teamBg = team.mine ? TEAM_MINE_BG : TEAM_BG;
                const rank = team.leader && salesRole !== 'executive' ? teamIdx + 1 : null;
                return (
                  <React.Fragment key={key}>
                    <TableRow
                      onClick={canExpand ? () => setCollapsedTeams((prev) => ({ ...prev, [key]: !prev[key] })) : undefined}
                      sx={{ cursor: canExpand ? 'pointer' : 'default', '& > td': { bgcolor: teamBg, fontWeight: 700, borderTop: `1px solid ${theme.palette.divider}` } }}
                    >
                      <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, whiteSpace: 'nowrap' }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {canExpand ? (
                            <IconButton size="small" sx={{ p: 0.25 }} aria-label={open ? 'Collapse team' : 'Expand team'}>
                              {open ? <ExpandedIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 28 }} />
                          )}
                          <span>{team.leader ? `Team ${team.leader}` : 'No team'}</span>
                          {rank && <Chip label={`#${rank}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                          {team.mine && <Chip label="Your team" size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />}
                          {team.detail && team.leader && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                              {members.length} {members.length === 1 ? 'person' : 'people'}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {REVENUE_COLUMNS.map((col) => (
                        <TableCell key={col.label} align="right">
                          {team.detail || col.inTotalOnly ? col.render(team.total) : <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>–</Box>}
                        </TableCell>
                      ))}
                    </TableRow>
                    {open && members.map((row) => (
                      <TableRow key={`${key}:${row.owner}`} hover>
                        <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, pl: 5.5, whiteSpace: 'nowrap' }}>
                          {row.owner}
                          {team.leader && row.owner.toLowerCase() === team.leader.toLowerCase() && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>Team leader</Typography>
                          )}
                        </TableCell>
                        {REVENUE_COLUMNS.map((col) => (
                          <TableCell key={col.label} align="right">{col.render(row)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {teams && salesRole === 'leader' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Other teams show their total revenue and conversions only.
          </Typography>
        )}
        {teams && salesRole === 'executive' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Your team's executives; the team leader is not included in the rows or the total.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default RevenueDashboard;
